class UrlUtils{
    /**
     * Adds a query parameter to the current URL and updates the browser's history state.
     *
     * @param {string} key - The name of the query parameter to add.
     * @param {string} value - The value of the query parameter to add.
     */
    static addQueryParam(key, value) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set(key, value);
        window.history.replaceState({}, '', url);
    }

    /**
     * Removes a query parameter from the current URL.
     *
     * @param {string} key - The key of the query parameter to remove.
     */
    static removeQueryParam(key) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(key);
        window.history.replaceState({}, '', url);
    }

    /**
     * Retrieves the query parameters from the current URL.
     *
     * @returns {URLSearchParams} An instance of URLSearchParams containing the query parameters.
     */
    static getQueryParams() {
        return new URLSearchParams(window.location.search);
    }

    /**
     * Retrieves the value of a specified query parameter from the current URL.
     *
     * @param {string} key - The name of the query parameter to retrieve.
     * @returns {string|null} The value of the query parameter, or null if the parameter is not present.
     */
    static getQueryParam(key) {
        return new URLSearchParams(window.location.search).get(key);
    }
}
class CookieUtils{
    /**
     * Retrieves the value of a specified cookie by its name.
     *
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|undefined} The value of the cookie if found, otherwise undefined.
     */
    static getCookie(name) {
        const value = "; " + document.cookie;
        const parts = value.split("; " + name + "=");
        if (parts.length == 2) return parts.pop().split(";").shift();
    }

    /**
     * Sets a cookie with the specified name, value, and expiration days.
     *
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value of the cookie.
     * @param {number} days - The number of days until the cookie expires.
     * @param {string} [domain=null] - The domain for which the cookie is valid. Defaults to null.
     */
    static setCookie(name, value, days, domain = null) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = "; expires=" + date.toUTCString();
        }
        if (domain) {
            document.cookie = name + "=" + value + expires + "; domain=" + domain + "; path=/";
        }
        else {
            document.cookie = name + "=" + value + expires + "; path=/";
        }
    }
    /**
     * Deletes a cookie with the specified name.
     *
     * @param {string} name - The name of the cookie to delete.
     */
    static deleteCookie(name) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
}
class LocalStorageUtils{
    /**
     * Stores a value in the local storage.
     *
     * @param {string} key - The key under which the value is stored.
     * @param {string | object} value - The value to be stored.
     */
    static setItem(key, value) {
        localStorage.setItem(key, value);
    }
    /**
     * Retrieves an item from local storage.
     *
     * @param {string} key - The key of the item to retrieve.
     * @returns {string | null} The value of the item, or null if the item does not exist.
     */
    static getItem(key) {
        try {
            const storedData = localStorage.getItem(key);
            return storedData ? JSON.parse(storedData) : null;
        } catch (error) {
            console.error(`Error retrieving data from localStorage for key "${key}":`, error);
            return null; // Error occurred while retrieving data
        }
    }
    // Remove data from localStorage
    static removeItem(key) {
        try {
            localStorage.removeItem(key);
            return true; // Data successfully removed
        } catch (error) {
            console.error(`Error removing data from localStorage for key "${key}":`, error);
            return false; // Error occurred while removing data
        }
    }

  // Clear all data in localStorage
  static clear() {
        try {
            localStorage.clear();
            return true; // localStorage cleared successfully
        } catch (error) {
            console.error("Error clearing localStorage:", error);
            return false; // Error occurred while clearing localStorage
        }
  }
}
class DateUtils{
    /**
     * Gets the current date formatted as 'YYYY-MM-DD' in the specified timezone.
     * https://timeapi.io/documentation/iana-timezones
     *
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {string} The current date in the format 'YYYY-MM-DD'.
     */
    static getCurrentDate(timezone) {
        const date = new Date();
        const year = date.toLocaleString('default', {
            timeZone: timezone,
            year: 'numeric'
        });
        const month = date.toLocaleString('default', {
            timeZone: timezone,
            month: '2-digit',
        });
        const day = date.toLocaleString('default', {
            timeZone: timezone,
            day: '2-digit'
        });

        return [year, month, day].join('-');
    }

    /**
     * Checks if the current date is between the given start and end dates.
     *
     * @param {Date} startDate - The start date to compare.
     * @param {Date} endDate - The end date to compare.
     * @returns {boolean} - Returns true if the current date is between the start and end dates, otherwise false.
     */
    static checkDateBetween(timezone, startDate, endDate) {
        if (!startDate || !endDate) return true;
        const date = this.getCurrentDate(timezone);
        return date >= startDate && date <= endDate;
    }

    /**
     * Gets the current date time formatted as 'YYYY-MM-DD hh:mm:ss' in the specified timezone.
     *
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {string} - The current date time in the format 'YYYY-MM-DD hh:mm:ss'.
     */
    static getCurrentDateTime(timezone) {
        const date = new Date();
        let year = date.toLocaleString('default', {
            timeZone: timezone,
            year: 'numeric'
        });
        let month = date.toLocaleString('default', {
            timeZone: timezone,
            month: '2-digit',
        });
        let day = date.toLocaleString('default', {
            timeZone: timezone,
            day: '2-digit'
        });

        let hour = date.toLocaleTimeString('default', {
            timeZone: timezone,
            hour: '2-digit',
            hour12: false
        });

        let minute = date.toLocaleTimeString('default', {
            timeZone: timezone,
            minute: '2-digit'
        });
        let second = date.toLocaleTimeString('default', {
            timeZone: timezone,
            second: '2-digit'
        });

        if (minute.length == 1) {
            minute = '0' + minute;
        }
        if (second.length == 1) {
            second = '0' + second;
        }

        return [year, month, day].join('-') + ' ' + [hour, minute, second].join(':');
    }


    /**
     * Returns an array containing the values to be shown in the countdown
     *
     * @param {Date} countDownDate - The date that i want to set the countdown for
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {Array} - Returns an array of three elements like this ["24h","59m","59s"]
     */
    static getCountdown(countDownDate, timezone) {
        const currentDateTime = this.getCurrentDateTime(timezone);
        const currentTimestamp = new Date(currentDateTime.replace(' ', 'T')).getTime();
        const distance = countDownDate - currentTimestamp;

        if (distance < 0) {
            return [];
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        let countdownValues = [];

        if (days > 0) {
            countdownValues.push(days + "d");
            countdownValues.push(hours + "h");
            countdownValues.push(minutes + "m");
            countdownValues.push(seconds + "s");
        } else {
            if (hours > 0) {
                countdownValues.push(hours + "h");
                countdownValues.push(minutes + "m");
                countdownValues.push(seconds + "s");
            } else {
                if (minutes > 0) {
                    countdownValues.push(minutes + "m");
                    countdownValues.push(seconds + "s");
                } else {
                    countdownValues.push(seconds + "s");
                }
            }
        }

        return countdownValues;
    }
}
class Fetchutils{

    /**
     * Fetches data from a specified URL and returns the response as JSON.
     *
     * @param {string} url - The URL to fetch the data from.
     * @returns {Promise<any>} A promise that resolves with the JSON response data.
     */
    static async fetchJson(url) {
        const response = await fetch(url);
        return response.json();
    }

    /**
     * Fetches HTML content from a given URL and parses it into a Document object.
     *
     * @param {string} url - The URL to fetch the HTML content from.
     * @returns {Promise<Document>} A promise that resolves to a Document object containing the parsed HTML.
     */
    static async fetchHtml(url) {
        const response = await fetch(url);
        const text = await response.text();
        return new DOMParser().parseFromString(text, 'text/html');
    }

    /**
     * Sends a POST request with JSON data to the specified URL and returns the JSON response.
     *
     * @param {string} url - The URL to send the POST request to.
     * @param {Object} data - The data to be sent in the body of the POST request.
     * @returns {Promise<Object>} - A promise that resolves to the JSON response.
     */
    static async postJson(url, data) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        return response.json();
    }

    /**
     * Sends a POST request to a GraphQL endpoint.
     *
     * @param {string} url - The URL of the GraphQL endpoint.
     * @param {string} token - The Shopify Storefront Access Token.
     * @param {string} query - The GraphQL query to be sent.
     * @returns {Promise<Object>} - A promise that resolves to the JSON response from the server.
     */
    static async postGraphql(url, token, query) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                //'Content-Type': 'application/graphql',
                'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({ query })
        });

        return response.json();
    }

}
class ObserverUtils{
    /**
     * Creates a new IntersectionObserver instance.
     *
     * @param {IntersectionObserverCallback} callback - The function to be called when the observed elements intersect with the viewport.
     * @param {IntersectionObserverInit} options - Optional configuration options for the IntersectionObserver.
     * @returns {IntersectionObserver} A new IntersectionObserver instance.
     */
    static createIntersectionObserver(callback, options) {
        return new IntersectionObserver(callback, options);
    }

    /**
     * Creates a new MutationObserver instance.
     *
     * @param {MutationCallback} callback - The function to be called when mutations are observed.
     * @param {MutationObserverInit} options - Options for configuring the observer.
     * @returns {MutationObserver} A new MutationObserver instance.
     */
    static createMutationObserver(callback, options) {
        return new MutationObserver(callback, options);
    }
}
class PubSub{
    static subscribers = {
        'SundekLocalizationForm-popup' : []
    };
    /**
     * Publishes an event to all subscribers.
     *
     * @param {string} eventName - The name of the event to publish.
     * @param {*} data - The data to pass to the event subscribers.
     */
    static publish(eventName, data) {
        if (PubSub.subscribers[eventName]) {
            PubSub.subscribers[eventName].forEach((callback) => {
                callback(data);
            });
        }
    }

    /**
     * Subscribes a callback function to a specific event.
     *
     * @param {string} eventName - The name of the event to subscribe to.
     * @param {Function} callback - The callback function to be executed when the event is triggered.
     * @returns {Function} A function to unsubscribe the callback from the event.
     */
    static subscribe(eventName, callback) {
        if (PubSub.subscribers[eventName] === undefined) {
            PubSub.subscribers[eventName] = [];
        }

        PubSub.subscribers[eventName] = [...PubSub.subscribers[eventName], callback];

        return function unsubscribe() {
            PubSub.subscribers[eventName] = PubSub.subscribers[eventName].filter((cb) => {
                return cb !== callback;
            });
        };
    }
}
class TimingUtils{

    /* Debouncing ensures that a function is not called
    again until a certain amount of time has passed
    since the last call.
    This is useful for scenarios like search input
    fields where you want to wait until the user has
    stopped typing before making an API call.

    Usage:
    const search = debounce((query) => {
        console.log(`Searching for ${query}`);
        // Imagine an API call here
    }, 300);
    */
    static debounce(func, delay) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }

    /* Throttling ensures that a function is called
    at most once in a specified time period.
    This is useful for scenarios like scroll events
    where you want to limit the frequency
    of function calls.

    USAGE:
    const handleScroll = throttle(() => {
        console.log('Scrolled');
        // Imagine complex calculations or DOM updates here
    }, 300);
    */
    static throttleed(func, interval) {
        let lastCall = 0;
        return function(...args) {
            const now = Date.now();
            if (now - lastCall >= interval) {
                lastCall = now;
                func.apply(this, args);
            }
        };
    }

    static throttle(func, limit) {
        let lastFunc; // Ultima funzione eseguita
        let lastRan;  // Ultimo momento in cui la funzione è stata eseguita

        return function(...args) {
            const context = this; // Contesto della funzione

            if (!lastRan) {
                // Se non è mai stata eseguita, esegui subito
                func.apply(context, args);
                lastRan = Date.now();
            } else {
                clearTimeout(lastFunc); // Cancella il timeout precedente
                lastFunc = setTimeout(() => {
                    // Controlla se il tempo limite è passato
                    if ((Date.now() - lastRan) >= limit) {
                        func.apply(context, args); // Esegui la funzione
                        lastRan = Date.now(); // Aggiorna il timestamp
                    }
                }, limit - (Date.now() - lastRan));
            }
        };
    }
}
class ViewportClasses {

    mediaQueries = {
        'sm'       : '(min-width: 320px)',
        'md'       : '(min-width: 768px)',
        'lg'       : '(min-width: 1024px)',
        'xl'       : '(min-width: 1920px)',
        'mobile'   : '(min-width: 320px) and (max-width: 767px)',
        'tablet'   : '(min-width: 768px) and (max-width: 1023px)',
        'desktop'  : '(min-width: 1024px)',
        'landscape': '(orientation: landscape)',
        'portrait' : '(orientation: portrait)'
    }

    constructor() {
        this.htmlElement = document.documentElement;
        this.updateViewportClass();
        window.addEventListener('resize', TimingUtils.debounce(() => this.updateViewportClass(), 250));
    }

    updateViewportClass() {
        // row by row
        for (const [key, query] of Object.entries(this.mediaQueries)) {
            // Remove class by class
            this.htmlElement.classList.remove(key);
            if (window.matchMedia(query).matches) {
                // Add right classes
                this.htmlElement.classList.add(key);
            }
        }
    }
}
new ViewportClasses();
class TextUtils{

    /**
     * console.log(capitalizeText('learn to rest, not to quit'));
     * Output: Learn To Rest, Not To Quit
     */
    static capitalizeText(text) {
        return text
                .toLowerCase()
                .replace(/(^|\s)\w/g, c => c.toUpperCase());
    };

    /**
     * console.log(validateFullName('Brendan Eich'));
     * Output: Valid input
     *
     * console.log(validateFullName('James Bond 007'));
     * Output: Invalid input
     *
     * for email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
     *
     * for password: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).{8,}$/
     * Password must contain at least 8 characters, including at least one uppercase letter, one lowercase letter, one number, and one symbol
     */
    static validateInput(value, regex = /^[a-zA-Z ]+$/) {
        if (!value) return "This field must not be empty.";
        if (!regex.test(value)) {
            return "Invalid input";
        }
        return "Valid input";
    };

    static getTruncateDescription(description, maxLength) {
        if (description.length <= maxLength) {
            return description;
        }

        const truncated = description.substring(0, maxLength - 3);
        return truncated + '...';
    }

}
